import React, { useEffect, useState } from "react";
import { Plus, Pencil } from "@/components/icons";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { ErrorState } from "@/components/ListState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

// Round 28 §2.1: managed keuringtype register. Owner/TD add, rename, deactivate.
// `voertuig` carries the APK behaviour off the name — a rename never changes
// what the app does.
export const SettingsKeuringtypes = () => {
  const { canWrite } = useAuth();
  const writable = canWrite();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const EMPTY = { naam: "", voertuig: false, standaard_frequentie_maanden: "" };
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true); setLoadError("");
    try {
      const { data } = await api.get("/inspection-types?include_inactive=true");
      setItems(data);
    } catch (err) {
      setLoadError(getErrorMessage(err, "Keuringtypes konden niet geladen worden."));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ naam: t.naam, voertuig: Boolean(t.voertuig), standaard_frequentie_maanden: t.standaard_frequentie_maanden ? String(t.standaard_frequentie_maanden) : "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.naam.trim()) { toast.error("Geef een naam."); return; }
    const freq = form.standaard_frequentie_maanden ? Number(form.standaard_frequentie_maanden) : null;
    setSaving(true);
    try {
      const body = { naam: form.naam, voertuig: form.voertuig, standaard_frequentie_maanden: freq };
      if (editing) await api.put(`/inspection-types/${editing.id}`, body);
      else await api.post("/inspection-types", body);
      toast.success(editing ? "Keuringstype bijgewerkt" : "Keuringstype toegevoegd");
      setOpen(false);
      await load();
    } catch (e) { toast.error(getErrorMessage(e, "Opslaan mislukt.")); }
    finally { setSaving(false); }
  };

  const toggleActive = async (t, active) => {
    try { await api.put(`/inspection-types/${t.id}`, { active }); await load(); }
    catch (e) { toast.error(getErrorMessage(e, "Wijzigen mislukt.")); }
  };

  if (loadError) return <ErrorState testid="keuringtypes-load-error" message={loadError} onRetry={load} />;

  return (
    <div className="space-y-4" data-testid="settings-keuringtypes">
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h2 className="text-body font-semibold text-foreground">Keuringtypes</h2>
            <p className="text-data text-muted-foreground">Welke keuringen we bijhouden — hernoembaar, deactiveerbaar. "Voertuig" toont het kenteken en zet een nieuwe keuring op te controleren.</p>
          </div>
          {writable && (
            <Button size="sm" data-testid="keuringtype-toevoegen-button" onClick={openCreate}>
              <Plus size={16} className="mr-1.5" /> Type toevoegen
            </Button>
          )}
        </div>
        {loading ? (
          <p className="text-body text-muted-foreground">Laden…</p>
        ) : items.length === 0 ? (
          <p className="text-body text-muted-foreground">Nog geen keuringtypes.</p>
        ) : (
          <>
          <Table className="hidden sm:table" data-testid="keuringtypes-table">
            <TableHeader>
              <TableRow>
                <TableHead>Naam</TableHead>
                <TableHead className="w-[90px]">Voertuig</TableHead>
                <TableHead className="w-[110px]">Actief</TableHead>
                <TableHead className="w-[80px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((t) => (
                <TableRow key={t.id} data-testid={`keuringtype-row-${t.id}`}>
                  <TableCell className="text-body">{t.naam}</TableCell>
                  <TableCell className="text-data text-muted-foreground">{t.voertuig ? "ja" : ""}</TableCell>
                  <TableCell>
                    <Switch
                      data-testid={`keuringtype-active-${t.id}`}
                      checked={Boolean(t.active)}
                      onCheckedChange={(v) => toggleActive(t, v)}
                      disabled={!writable}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    {writable && (
                      <Button size="sm" variant="ghost" data-testid={`keuringtype-edit-${t.id}`} onClick={() => openEdit(t)}>
                        <Pencil size={16} />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {/* Rule 21: below sm the table becomes cards. */}
          <div className="divide-y divide-border sm:hidden" data-testid="keuringtypes-cardlist">
            {items.map((t) => (
              <div key={t.id} data-testid={`keuringtype-card-${t.id}`} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <div className="text-body text-foreground">{t.naam}</div>
                  <div className="text-data text-muted-foreground">{t.voertuig ? "voertuigkeuring" : "\u00a0"}</div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Switch
                    data-testid={`keuringtype-card-active-${t.id}`}
                    checked={Boolean(t.active)}
                    onCheckedChange={(v) => toggleActive(t, v)}
                    disabled={!writable}
                  />
                  {writable && (
                    <Button size="sm" variant="ghost" data-testid={`keuringtype-card-edit-${t.id}`} onClick={() => openEdit(t)}>
                      <Pencil size={16} />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent srDescription={editing ? "Hernoem dit keuringstype." : "Voeg een nieuw keuringstype toe."}>
          <DialogHeader><DialogTitle>{editing ? "Keuringstype hernoemen" : "Keuringstype toevoegen"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Naam</Label>
              <Input data-testid="keuringtype-naam-input" value={form.naam} onChange={(e) => setForm((f) => ({ ...f, naam: e.target.value }))} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Voertuigkeuring</Label>
                <p className="text-data text-muted-foreground">Toont het kenteken; nieuwe keuring start op te controleren.</p>
              </div>
              <Switch data-testid="keuringtype-voertuig-switch" checked={form.voertuig} onCheckedChange={(v) => setForm((f) => ({ ...f, voertuig: v }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Standaard frequentie (maanden, optioneel)</Label>
              <Input type="number" min="1" data-testid="keuringtype-freq-input" value={form.standaard_frequentie_maanden} onChange={(e) => setForm((f) => ({ ...f, standaard_frequentie_maanden: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Annuleren</Button>
            <Button data-testid="keuringtype-save-button" onClick={save} disabled={saving}>{saving ? "Bezig…" : "Opslaan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
